import { useState } from "react";
import type { ParticipationPromptPublic } from "@tih/shared";
import { Link } from "react-router-dom";
import { ApiError, postParticipationAggregate } from "../../api/client";
import { useEventContent } from "../../context/EventContentContext";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { getAtPath } from "../../lib/siteCopyPaths";
import { setRsvpAck } from "../../lib/rsvpAck";
import { useRsvpReservation } from "../../hooks/useRsvpReservation";
import { useBotProtection } from "../../hooks/useBotProtection";
import { RsvpModal } from "../RsvpModal";
import { HoneypotField } from "../forms/HoneypotField";
import { TurnstileWidget } from "../forms/TurnstileWidget";
import { formatRsvpThanksLine, hasFeaturedSchedule } from "../../lib/eventDisplay";

type Props = {
  prompt: ParticipationPromptPublic;
  registeredSeats: number;
  onUpdate: () => void;
};

export function ParticipationRsvpAction({ prompt, registeredSeats, onUpdate }: Props) {
  const { copy } = useSiteCopy();
  const content = useEventContent();
  const eventId = content.eventId;
  const reservation = useRsvpReservation(eventId);
  const hasSchedule = hasFeaturedSchedule(content);
  const rsvpClosed = Boolean(content.cancellation) && !hasSchedule;
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  async function confirmYes() {
    if (!ready || busy) return;
    setError(null);
    setBusy(true);
    try {
      await postParticipationAggregate({
        promptSlug: prompt.slug,
        yesNo: true,
        ...botFields,
      });
      setRsvpAck(eventId);
      onUpdate();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not save");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  if (rsvpClosed) {
    return (
      <div className="participation-rsvp-action">
        <p className="lede-serif" style={{ margin: 0 }}>
          {content.cancellation?.message ??
            "This overnight has been cancelled. RSVP will open again for the next gathering."}
        </p>
      </div>
    );
  }

  return (
    <div className="participation-rsvp-action">
      <HoneypotField value={website} onChange={setWebsite} />
      <p className="participation-social-proof">
        <b>{registeredSeats.toLocaleString()}</b>{" "}
        {String(getAtPath(copy, "home.participation.seatsReserved") ?? "seats reserved")}
      </p>
      {reservation ? (
        <div className="form-success">
          <strong>{String(getAtPath(copy, "home.participation.rsvpConfirmed") ?? "")}</strong>
          <p className="lede-serif" style={{ marginTop: 8 }}>
            {[
              reservation.name.trim() || null,
              `${reservation.seats} seat${reservation.seats === 1 ? "" : "s"}`,
              reservation.email || null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 8 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setRsvpOpen(true)}
            >
              Change reservation
            </button>
            <Link to="/participate" className="btn btn-ghost">
              Invite someone to sit with you →
            </Link>
          </p>
        </div>
      ) : (
        <div className="participation-yesno-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !ready}
            onClick={() => void confirmYes()}
          >
            {String(getAtPath(copy, "home.participation.rsvpYes") ?? "Yes, I'm coming")}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => setRsvpOpen(true)}
          >
            {String(getAtPath(copy, "home.participation.rsvpNotYet") ?? "Not yet — reserve a seat")}
          </button>
        </div>
      )}
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      {error && <div className="form-error">{error}</div>}
      <RsvpModal
        open={rsvpOpen}
        onClose={() => setRsvpOpen(false)}
        eventId={eventId}
        onSuccess={onUpdate}
        thanksLine={
          hasSchedule
            ? formatRsvpThanksLine(content.date)
            : "See you at the next worship overnight."
        }
        shareDateIso={hasSchedule ? content.date : undefined}
        shareEventTitle={content.eventTitle}
      />
    </div>
  );
}
